import React, { useEffect, useState, useCallback } from "react";
import { Box, Typography, Pagination } from "@mui/material";
import DataTable from "../../common/DataTable";
import { fetchAssignedOutlets } from "../../api/userAPI";
import CustomDateRangePicker from "../../pages/selller/dashboards/components/custom-date-range";
import { useNavigate } from "react-router-dom";
import { checkDetailsStyles } from "../customerManagement/DisplayCustomers.styles";

interface OutletData {
  id: string;
  customerId: string;
  outletName: string;
  firstName?: string;
  lastName?: string;
  email?: string;
  emailId: string;
  createdAt: string;
  assignedOn?: string;
}
import CloudDownloadIcon from "../../assets/download-cloud-02.svg";
import SortBy from "../../common/filters/SortBy";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import colors from "../../theme/Colours";
import PageHeader from "../../common/PageHeader";

import {
  containerStyle,
  searchAndSortContainer,
  mainContentStyle,
  paginationContainerStyle,
  paginationCaptionStyle,
  downloadCsvButtonStyle,
  downloadIconStyle,
  SortBYContainerStyle,
} from "../customerManagement/DisplayCustomers.styles";
import moment from "moment";
import SearchBar from "../../common/filters/SearchBar";
import { exportToExcel } from "../../utils/exportToExcel";
import { useOutsideClose } from "../../hooks/useOutsideClose";

type SortTypes = "newest" | "oldest";

const AssignedOutlets = () => {
  const [outlets, setOutlets] = useState<OutletData[]>([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState("");
  const options = ["Newest", "Oldest"];
  const [sortBy, setSortBy] = useState<string>("");
  const [sortOrder, setSortOrder] = useState<SortTypes>("newest");

  const [pagination, setPagination] = useState({
    page: 1,
    rowsPerPage: 10,
    totalRows: 0,
    totalPages: 0,
  });

  const [from, setFrom] = useState<Date | undefined>(undefined);
  const [to, setTo] = useState<Date | undefined>(undefined);

  const [openFilter, setOpenFilter] = React.useState<"sort" | "date" | null>(
    null
  );
  const filterContainerRef = React.useRef<HTMLDivElement>(null);

  useOutsideClose(filterContainerRef, openFilter !== null, () =>
    setOpenFilter(null)
  );

  const navigate = useNavigate();
  const orgId = localStorage.getItem("orgId");

  const columns = [
    { label: "Name", field: "name" },
    { label: "Created Date", field: "createdAt" },
    { label: "Email", field: "email" },
    { label: "Assigned On", field: "assignedOn" },
    { label: "Ledger", field: "ledger" },
  ];

  useEffect(() => {
    if (!sortBy) {
      setSortOrder("newest");
      return;
    }
    setSortOrder(sortBy.toLowerCase() === "oldest" ? "oldest" : "newest");
  }, [sortBy]);

  const fetchOutletData = useCallback(
    async (download: string, search?: string, page?: number) => {
      const userEmail = localStorage.getItem("userEmail") || "";
      const apiSortOrder = sortOrder === "newest" ? "DESC" : "ASC";

      const adjustedFromDate = from
        ? moment(from).startOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : undefined;

      const adjustedToDate = to
        ? moment(to).endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]")
        : undefined;

      return await fetchAssignedOutlets({
        userEmail,
        page: download === "true" ? undefined : page,
        limit: download === "true" ? undefined : pagination.rowsPerPage,
        search: search?.toLowerCase(),
        startDate: adjustedFromDate,
        endDate: adjustedToDate,
        download,
        sortOrder: apiSortOrder,
      });
    },
    [sortOrder, from, to, pagination.rowsPerPage]
  );

  const generateExcel = async () => {
    try {
      const response = await fetchOutletData("true", searchTerm);

      if (!response?.data) {
        console.error("No data received from API");
        return;
      }

      const allOutlets = response.data.data.assignedOutlets;

      const excelData = allOutlets.map(
        (outlet: {
          name: string;
          createdAt: string;
          email: string;
          assignedOn: string;
        }) => ({
          Name: outlet.name,
          "Created Date": moment(outlet.createdAt).format(
            "DD MMM YY hh:mm:ss A"
          ),
          Email: outlet.email,
          "Assigned On": moment(outlet.assignedOn).format(
            "DD MMM YY hh:mm:ss A"
          ),
        })
      );

      await exportToExcel(excelData, "assigned-outlets.xlsx");
    } catch (error) {
      console.error("Error generating Excel:", error);
    }
  };

  const loadOutlets = useCallback(
    async (page: number, search?: string) => {
      try {
        const response = await fetchOutletData("false", search, page);

        if (response.data.data) {
          setPagination({
            page: response.data.data.pagination.currentPage,
            rowsPerPage: response.data.data.pagination.limit,
            totalRows: response.data.data.pagination.totalRecords,
            totalPages: response.data.data.pagination.totalPages,
          });
          const outletData = response.data.data.assignedOutlets.map(
            (outlet: {
              customerId: string;
              name: string;
              email: string;
              createdAt: string;
              assignedOn: string;
              [key: string]: unknown;
            }) => ({
              ...outlet,
              outletName: outlet.name,
              customerId: outlet.customerId,
              firstName: outlet.name.split(" ")[0],
              lastName: outlet.name.split(" ")[1] || "",
              emailId: outlet.email,
              createdAt: moment(outlet.createdAt).format(
                "DD MMM YY hh:mm:ss A"
              ),
              assignedOn: moment(outlet.assignedOn).format(
                "DD MMM YY hh:mm:ss A"
              ),
            })
          );

          setOutlets(outletData);
        }
      } catch (error) {
        console.error("Error fetching outlets:", error);
      }
    },
    [fetchOutletData]
  );

  useEffect(() => {
    const t = setTimeout(() => setDebouncedSearchTerm(searchTerm), 1500);
    return () => clearTimeout(t);
  }, [searchTerm]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    loadOutlets(1, debouncedSearchTerm);
  }, [sortOrder, debouncedSearchTerm, loadOutlets]);

  useEffect(() => {
    setPagination((p) => ({ ...p, page: 1 }));
    loadOutlets(1, debouncedSearchTerm);
  }, [from, to, debouncedSearchTerm, loadOutlets]);

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    const searchValue = e.target.value;
    setSearchTerm(searchValue);
    setPagination((prev) => ({ ...prev, page: 1 }));
  };

  const handleChangePage = (newPage: number) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadOutlets(newPage, debouncedSearchTerm);
  };

  const redirectToLedger = (customerId: string) => {
    navigate(`/maltora-sales-rep/ledgers/${customerId}`);
  };

  const { page, rowsPerPage, totalPages, totalRows } = pagination;
  const startIndex = (page - 1) * rowsPerPage;
  const endIndex = startIndex + outlets.length;

  const tableData = outlets.map((outlet) => ({
    name: (
      <Typography component="span">
        {`${outlet.firstName || ""} ${outlet.lastName || ""}`}
      </Typography>
    ),
    createdAt: outlet.createdAt,
    email: outlet.email || outlet.emailId,
    assignedOn: outlet.assignedOn || "Not assigned",
    ledger: (
      <button
        onClick={(e) => {
          e.stopPropagation();
          redirectToLedger(outlet.customerId);
        }}
        style={checkDetailsStyles}
      >
        Check Details
      </button>
    ),
  }));

  return (
    <>
      <PageHeader title="Assigned To" />
      <Box sx={containerStyle}>
        {/* Search and Sort */}
        <Box sx={searchAndSortContainer}>
          {/* Search bar */}
          <Box sx={{ flex: 1 }}>
            <SearchBar searchTerm={searchTerm} handleSearch={handleSearch} />
          </Box>
          <Box
            ref={filterContainerRef}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 2,
              position: "relative",
              zIndex: openFilter === "date" ? 998 : undefined,
            }}
          >
            <SortBy
              containerSx={SortBYContainerStyle}
              options={options}
              selected={sortBy}
              setSelected={setSortBy}
              variant="seller"
              hideOptions
              isOpen={openFilter === "sort"}
              onToggle={(open) => setOpenFilter(open ? "sort" : null)}
            />

            <CustomDateRangePicker
              fromDate={from}
              toDate={to}
              setFromDate={setFrom}
              setToDate={setTo}
              placeholder="Select date range"
              disableFuture
              showReset
              sx={{
                height: "38px",
                width: "220px",
              }}
            />

            <button
              onClick={generateExcel}
              style={downloadCsvButtonStyle as React.CSSProperties}
            >
              <img
                src={CloudDownloadIcon}
                alt="Download"
                style={downloadIconStyle}
              />
              Download
            </button>
          </Box>
        </Box>

        {/* DataTable */}
        <Box sx={mainContentStyle}>
          <DataTable
            columns={columns.map((col) => ({
              ...col,
              label: (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                  }}
                >
                  <span>{col.label}</span>
                  <ArrowDownwardIcon
                    fontSize="small"
                    sx={{ color: colors.customerTablelabel }}
                  />
                </span>
              ),
              labelStyle: {
                fontSize: "14px",
                fontWeight: 500,
                color: colors.customerTablelabel,
                backgroundColor: colors.customerTableHeaderBG,
                "&.MuiTableCell-root.MuiTableCell-root": {
                  padding: "12px 24px",
                },
              },
              cellStyle: {
                fontSize: "14px",
                "&.MuiTableCell-root.MuiTableCell-root": {
                  padding: "12px 24px",
                },
                color:
                  col.field !== "name" ? colors.customerTablelabel : undefined,
              },
            }))}
            data={tableData}
            emptyStateMessage="No outlets available"
          />
        </Box>
      </Box>
      <Box>
        <Box sx={paginationContainerStyle}>
          <Typography variant="subtitle2" sx={paginationCaptionStyle}>
            Showing data {startIndex + 1} to {endIndex} of {totalRows} entries
          </Typography>
          <Pagination
            count={totalPages}
            page={page}
            onChange={(_, newPage) => handleChangePage(newPage)}
            shape="rounded"
            variant="outlined"
          />
        </Box>
      </Box>
    </>
  );
};

export default AssignedOutlets;
